// Pure helpers for the Daten-Bot widget. No hooks, no i18n instance — a
// translator function is passed in where copy is needed so this file stays
// framework-free and trivially testable.

import type { BotAnswer } from './api/botApi'

/** Minimal structural shape of react-i18next's `t` — avoids depending on
 *  i18next's (occasionally finicky) generic TFunction typing in a pure-util file. */
type Translator = (key: string, options?: Record<string, unknown>) => string

const EUR_4DP = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
const DE_INT = new Intl.NumberFormat('de-DE')

/**
 * Formats a cost given in micro-EUR (1e-6 EUR) as a 4-decimal de-DE amount,
 * e.g. 12300 -> "0,0123 €". Per Design Spec §8, an unpriced/unknown cost is
 * NEVER shown as "0,0000 €" — callers must pass the answer's `cost_status`.
 * `t` is optional (keeps this callable without an i18next instance, e.g. in
 * tests) — omitting it falls back to the German copy; pass it (bot.json's
 * `answer.priceUnknown`) so the fallback follows the active locale too.
 */
export function formatMicroEur(micro: number | null, status: 'computed' | 'unknown', t?: Translator): string {
  if (status !== 'computed' || micro == null) return t ? t('answer.priceUnknown') : 'Preis unbekannt'
  return `${EUR_4DP.format(micro / 1_000_000)} €`
}

/** de-DE thousands-grouped integer, e.g. 1234 -> "1.234". */
export function formatTokens(n: number): string {
  return DE_INT.format(n)
}

/** de-DE amount with a fixed decimal count, e.g. formatEur(12.5, 2) -> "12,50". */
export function formatEur(value: number, decimals: number): string {
  return value.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

/** True once an answer can no longer change state. */
export function isTerminal(status: BotAnswer['status']): boolean {
  return status === 'done' || status === 'failed' || status === 'cancelled'
}

/** True while any answer on the turn is still queued/running — the exact
 *  shape `useBotTurn`'s poll target (`BotTurn`) and each entry of a
 *  conversation's `turns[]` both satisfy. */
export function anyNonTerminal(
  turn: { answers: readonly Pick<BotAnswer, 'status'>[] } | null | undefined,
): boolean {
  if (!turn) return false
  return turn.answers.some((a) => !isTerminal(a.status))
}

/** Conversation-wide version of `anyNonTerminal` — true while any turn in
 *  the conversation still has a non-terminal answer. Used by BotPanel's
 *  `useBotConversation` poll while the panel is open. */
export function conversationHasNonTerminal(
  conversation: { turns: readonly { answers: readonly Pick<BotAnswer, 'status'>[] }[] } | null | undefined,
): boolean {
  if (!conversation) return false
  return conversation.turns.some((t) => anyNonTerminal(t))
}

/** Short, translated one-liner for a finished answer — used for the
 *  "answered" toast and anywhere else a compact per-answer label is needed. */
export function answerHeadline(answer: Pick<BotAnswer, 'modelLabel'>, t: Translator): string {
  return t('toast.answered', { model: answer.modelLabel })
}
